import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    Divider,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    LinearProgress,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    FileUpload as FileUploadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import React, { useState, useRef, useEffect, memo } from 'react';
import { useGetPromotionTriggerSettingsInfinite } from '../../../../client/hooks/products/trigger.queries';

const PROMOTION_TYPES = [
    'DEAL', 'SMART', 'PRICE_DISCOUNT', 'VOLUME', 'PRE_NEGOTIATED', 
    'DOD', 'LIGHTNING', 'SELLER_CAMPAIGN', 'PRICE_MATCHING', 
    'UNHEALTHY_STOCK', 'SELLER_COUPON_CAMPAIGN'
];

const PROMOTION_TYPES_LABELS = {
    'DEAL': 'Oferta',
    'SMART': 'Inteligente',
    'PRICE_DISCOUNT': 'Desconto de Preço',
    'VOLUME': 'Volume',
    'PRE_NEGOTIATED': 'Pré-negociado',
    'DOD': 'Oferta do Dia',
    'LIGHTNING': 'Relâmpago',
    'SELLER_CAMPAIGN': 'Campanha do Vendedor',
    'PRICE_MATCHING': 'Equiparação de Preço',
    'UNHEALTHY_STOCK': 'Estoque Problemático',
    'SELLER_COUPON_CAMPAIGN': 'Campanha de Cupom do Vendedor'
};

const CONDITIONS = [
    { value: 'PRICE', label: 'Preço Sugerido' },
    { value: 'SELLER_PERCENTAGE', label: 'Percentual do Vendedor' },
    { value: 'MELI_PERCENTAGE', label: 'Percentual do Mercado Livre' },
    { value: 'ORIGINAL_PRICE', label: 'Preço Original' }
];

const OPERATORS = [
    { value: 'GREATER_THAN', label: 'Maior que' },
    { value: 'LESS_THAN', label: 'Menor que' },
    { value: 'EQUALS', label: 'Igual a' }
];

function TriggerForm({ 
    formData,
    editingTrigger,
    triggerSettings,
    onFormDataChange,
    onTriggerSettingsChange,
    onSubmit,
    onCancel,
    onRuleChange,
    onAddRule,
    onRemoveRule,
    onAddSetting,
    onRemoveSetting,
    onSettingChange,
    onImportedDataChange, // Nova prop para passar dados da planilha
    processingBatches, // Nova prop para mostrar progresso dos lotes
}) {
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [importedData, setImportedData] = useState(null); // Armazena dados da planilha sem renderizar
    const tableContainerRef = useRef(null);
    
    // Hook para carregar settings existentes com paginação infinita
    const {
        data: existingSettings,
        totalItems: totalExistingSettings,
        isLoading: isLoadingSettings,
        hasNextPage,
        loadNextPage,
        clearCache
    } = useGetPromotionTriggerSettingsInfinite(
        editingTrigger?._id,
        !!editingTrigger?._id
    );

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploadingFile(true);
        
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const newSettings = [];
                
                jsonData.forEach((row) => {
                    // Detectar colunas mlb_item e price com várias variações possíveis
                    const mlbItem = row.mlb_item || row.MLB || row['MLB Item'] || row['mlb item'] || 
                                   row['Item ID'] || row['item_id'] || row['id'] || row['ID'] || 
                                   Object.values(row)[0];
                    
                    const price = row.price || row.Price || row.min_price || row['Min Price'] || 
                                 row['min price'] || row.preco || row['Preço'] || row.valor ||
                                 row['Valor'] || row['deal_price'] || row['Suggested Discounted Price'] ||
                                 Object.values(row)[1];

                    if (mlbItem && !isNaN(parseFloat(price))) {
                        const cleanMlbItem = String(mlbItem).trim();
                        const cleanPrice = parseFloat(price);
                        
                        if (cleanMlbItem && cleanPrice >= 0) {
                            newSettings.push({
                                mlb_item: cleanMlbItem,
                                min_price: cleanPrice
                            });
                        }
                    }
                });
                
                if (newSettings.length === 0) {
                    alert('Nenhum dado válido encontrado na planilha. Verifique se existem as colunas mlb_item e price.');
                } else {
                    // Apenas armazenar os dados sem renderizar na tabela
                    const importData = {
                        fileName: file.name,
                        totalProcessed: jsonData.length,
                        validItems: newSettings.length,
                        invalidItems: jsonData.length - newSettings.length,
                        settings: newSettings // Dados prontos para envio
                    };
                    setImportedData(importData);
                    
                    // Passar dados para o componente pai
                    if (onImportedDataChange) {
                        onImportedDataChange(importData);
                    }
                    console.log(`${newSettings.length} produtos válidos processados da planilha`);
                }
                
            } catch (error) {
                console.error('Erro ao processar planilha:', error);
                alert('Erro ao processar arquivo. Verifique se é uma planilha válida.');
            } finally {
                setIsUploadingFile(false);
                event.target.value = '';
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const clearImportedData = () => {
        setImportedData(null);
        if (onImportedDataChange) {
            onImportedDataChange(null);
        }
    };
    
    // Função para detectar scroll no final da tabela
    const handleTableScroll = (event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.target;
        const threshold = 100; // 100px antes do final
        
        if (scrollHeight - scrollTop - clientHeight <= threshold && hasNextPage && !isLoadingSettings) {
            loadNextPage();
        }
    };
    
    // Combinar settings existentes (paginados) com settings manuais
    const allDisplaySettings = [
        ...(existingSettings?.map(setting => ({
            ...setting,
            isExisting: true, // Flag para identificar settings existentes
            mlb_item: setting.mlb_item,
            min_price: setting.min_price
        })) || []),
        ...triggerSettings.map((setting, index) => ({
            ...setting,
            isExisting: false,
            originalIndex: index // Para manter referência aos settings manuais
        }))
    ];
    
    // Limpar cache quando sair do modo de edição ou componente for desmontado
    useEffect(() => {
        return () => {
            // Cleanup quando o componente for desmontado
            clearCache();
        };
    }, [clearCache]);
    
    // Limpar cache quando mudar de trigger ou sair do modo de edição
    useEffect(() => {
        if (!editingTrigger) {
            clearCache();
        }
    }, [editingTrigger, clearCache]);
    return (
        <Card sx={{ 
            border: '1px solid #fff3e0',
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(255, 152, 0, 0.1)'
        }}>
            <CardContent sx={{ p: 4 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 4,
                    pb: 2,
                    borderBottom: '2px solid #fff3e0'
                }}>
                    <Box sx={{
                        width: 4,
                        height: 40,
                        bgcolor: '#ff9800',
                        borderRadius: 1
                    }} />
                    <Typography variant="h4" sx={{ 
                        color: '#ff9800', 
                        fontWeight: 700
                    }}>
                        {editingTrigger ? 'Editar Gatilho' : 'Novo Gatilho'}
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 4 }}>
                    <Box sx={{ flex: 1, minWidth: 280 }}>
                        <FormControl fullWidth>
                            <InputLabel>Tipo de Promoção</InputLabel>
                            <Select
                                value={formData.promotion_type}
                                label="Tipo de Promoção"
                                onChange={(e) => onFormDataChange({...formData, promotion_type: e.target.value})}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#ff9800'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#ff9800'
                                        }
                                    }
                                }}
                            >
                                {PROMOTION_TYPES.map(type => (
                                    <MenuItem key={type} value={type}>
                                        {PROMOTION_TYPES_LABELS[type] || type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    
                    <Box sx={{ flex: 1, minWidth: 280 }}>
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="Data de Expiração"
                            value={formData.expire_in}
                            onChange={(e) => onFormDataChange({...formData, expire_in: e.target.value})}
                            slotProps={{ inputLabel: { shrink: true } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#ff9800'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#ff9800'
                                    }
                                }
                            }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 4 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ 
                        fontWeight: 600,
                        color: '#333'
                    }}>
                        Regras do Gatilho
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={onAddRule}
                        size="small"
                        sx={{ 
                            textTransform: 'none',
                            borderColor: '#ff9800',
                            color: '#ff9800',
                            '&:hover': {
                                borderColor: '#f57c00',
                                bgcolor: 'rgba(255, 152, 0, 0.1)'
                            }
                        }}
                    >
                        Adicionar Regra
                    </Button>
                </Box>

                {formData.trigger_rules.map((rule, index) => (
                    <Card key={index} sx={{ 
                        mb: 2, 
                        bgcolor: '#fafafa', 
                        border: '1px solid #e0e0e0',
                        borderRadius: 2
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Condição</InputLabel>
                                        <Select
                                            value={rule.condition}
                                            label="Condição"
                                            onChange={(e) => onRuleChange(index, 'condition', e.target.value)}
                                        >
                                            {CONDITIONS.map(condition => (
                                                <MenuItem key={condition.value} value={condition.value}>
                                                    {condition.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Operador</InputLabel>
                                        <Select
                                            value={rule.operator}
                                            label="Operador"
                                            onChange={(e) => onRuleChange(index, 'operator', e.target.value)}
                                        >
                                            {OPERATORS.map(operator => (
                                                <MenuItem key={operator.value} value={operator.value}>
                                                    {operator.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                
                                <Box sx={{ flex: 1, minWidth: 120 }}>
                                    {rule.condition === 'PRICE' ? (
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Valor</InputLabel>
                                            <Select
                                                value={rule.value === 'mlb_trigger_settings' ? 'settings' : rule.value}
                                                label="Valor"
                                                onChange={(e) => {
                                                    const newValue = e.target.value === 'settings' ? 'mlb_trigger_settings' : parseFloat(e.target.value);
                                                    onRuleChange(index, 'value', newValue);
                                                }}
                                            >
                                                <MenuItem value="settings">Usar Settings (trigger_settings)</MenuItem>
                                                <MenuItem value={0}>0</MenuItem>
                                                <MenuItem value={50}>50</MenuItem>
                                                <MenuItem value={100}>100</MenuItem>
                                                <MenuItem value={200}>200</MenuItem>
                                                <MenuItem value={500}>500</MenuItem>
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Valor"
                                            value={rule.value}
                                            onChange={(e) => onRuleChange(index, 'value', parseFloat(e.target.value) || 0)}
                                        />
                                    )}
                                </Box>
                                
                                <Box>
                                    {formData.trigger_rules.length > 1 && (
                                        <IconButton 
                                            onClick={() => onRemoveRule(index)}
                                            color="error"
                                            size="small"
                                            sx={{
                                                '&:hover': {
                                                    transform: 'scale(1.1)'
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                ))}

                <Box sx={{ 
                    mt: 4, 
                    p: 3, 
                    bgcolor: '#fff8f0', 
                    borderRadius: 2,
                    border: '1px solid #fff3e0'
                }}>
                    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.active}
                                    onChange={(e) => onFormDataChange({...formData, active: e.target.checked})}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#ff9800'
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#ff9800'
                                        }
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontWeight: 600 }}>
                                    Gatilho Ativo
                                </Typography>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.apply_to_all_candidates}
                                    onChange={(e) => onFormDataChange({...formData, apply_to_all_candidates: e.target.checked})}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#4caf50'
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#4caf50'
                                        }
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ fontWeight: 600 }}>
                                    Aplicar a Todos os Candidatos
                                </Typography>
                            }
                        />
                    </Box>
                </Box>

                {!formData.apply_to_all_candidates && (
                    <Box sx={{ mt: 4 }}>
                        <Card sx={{ 
                            border: '1px solid #e3f2fd',
                            borderRadius: 3,
                            bgcolor: '#fafbff'
                        }}>
                            <CardContent sx={{ p: 4 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6" sx={{ 
                                        color: '#1976d2', 
                                        fontWeight: 600
                                    }}>
                                        Configurações por Produto (Settings)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <input
                                            accept=".xlsx,.xls"
                                            style={{ display: 'none' }}
                                            id="settings-upload"
                                            type="file"
                                            onChange={handleFileUpload}
                                            disabled={isUploadingFile}
                                        />
                                        <label htmlFor="settings-upload">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                startIcon={<FileUploadIcon />}
                                                size="small"
                                                disabled={isUploadingFile}
                                                sx={{ 
                                                    textTransform: 'none',
                                                    borderColor: '#1976d2',
                                                    color: '#1976d2',
                                                    opacity: isUploadingFile ? 0.6 : 1
                                                }}
                                            >
                                                {isUploadingFile ? 'Processando...' : 'Importar Planilha'}
                                            </Button>
                                        </label>
                                        <Button
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            onClick={onAddSetting}
                                            size="small"
                                            sx={{ 
                                                textTransform: 'none',
                                                borderColor: '#1976d2',
                                                color: '#1976d2'
                                            }}
                                        >
                                            Adicionar
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Resumo da planilha importada */}
                                {importedData && (
                                    <Alert 
                                        severity="success" 
                                        sx={{ 
                                            mt: 2, 
                                            borderRadius: 2,
                                            bgcolor: '#e8f5e8',
                                            border: '1px solid #4caf50'
                                        }}
                                        action={
                                            <Button
                                                size="small"
                                                onClick={clearImportedData}
                                                sx={{ color: '#2e7d32' }}
                                            >
                                                ✕
                                            </Button>
                                        }
                                    >
                                        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                                            📋 Planilha Processada: {importedData.fileName}
                                        </Typography>
                                        <Typography variant="body2">
                                            ✅ <strong>{importedData.validItems}</strong> produtos válidos processados
                                            {importedData.invalidItems > 0 && (
                                                <> • ❌ <strong>{importedData.invalidItems}</strong> inválidos ignorados</>
                                            )}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Os dados serão enviados quando você salvar o trigger
                                        </Typography>
                                    </Alert>
                                )}

                                {allDisplaySettings.length > 0 || totalExistingSettings > 0 ? (
                                    <>
                                        {(existingSettings?.length > 0 || totalExistingSettings > 0) && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {existingSettings?.length || 0} de {totalExistingSettings} settings existentes carregados
                                                    {triggerSettings.length > 0 && ` • ${triggerSettings.length} novos adicionados`}
                                                </Typography>
                                            </Box>
                                        )}
                                        <TableContainer 
                                            component={Paper}
                                            ref={tableContainerRef}
                                            onScroll={handleTableScroll}
                                            sx={{ 
                                                maxHeight: 300,
                                                borderRadius: 2,
                                                border: '1px solid #e0e0e0',
                                                overflow: 'auto'
                                            }}
                                        >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>MLB Item</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Preço Mínimo</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {allDisplaySettings.map((setting, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            {setting.isExisting ? (
                                                                <Typography sx={{
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.875rem',
                                                                    color: '#666',
                                                                    bgcolor: '#f5f5f5',
                                                                    p: 1,
                                                                    borderRadius: 1
                                                                }}>
                                                                    {setting.mlb_item}
                                                                </Typography>
                                                            ) : (
                                                                <TextField
                                                                    size="small"
                                                                    fullWidth
                                                                    placeholder="MLB123456789"
                                                                    value={setting.mlb_item}
                                                                    onChange={(e) => onSettingChange(setting.originalIndex, 'mlb_item', e.target.value)}
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-input': {
                                                                            fontFamily: 'monospace'
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {setting.isExisting ? (
                                                                <Typography sx={{
                                                                    fontSize: '0.875rem',
                                                                    color: '#666',
                                                                    bgcolor: '#f5f5f5',
                                                                    p: 1,
                                                                    borderRadius: 1,
                                                                    textAlign: 'center'
                                                                }}>
                                                                    R$ {setting.min_price?.toFixed(2) || '0.00'}
                                                                </Typography>
                                                            ) : (
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    fullWidth
                                                                    value={setting.min_price}
                                                                    onChange={(e) => onSettingChange(setting.originalIndex, 'min_price', parseFloat(e.target.value) || 0)}
                                                                    slotProps={{
                                                                        input: {
                                                                            startAdornment: <Typography sx={{ mr: 1, color: '#666' }}>R$</Typography>
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {setting.isExisting ? (
                                                                <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                                                                    Existente
                                                                </Typography>
                                                            ) : (
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => onRemoveSetting(setting.originalIndex)}
                                                                    sx={{
                                                                        '&:hover': {
                                                                            transform: 'scale(1.1)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                
                                                {/* Loader para próxima página */}
                                                {isLoadingSettings && (
                                                    <TableRow>
                                                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                                <CircularProgress size={16} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Carregando mais settings...
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                
                                                {/* Mensagem de final */}
                                                {!hasNextPage && existingSettings?.length > 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 1 }}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                Todos os settings existentes foram carregados
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                        </TableContainer>
                                    </>
                                ) : (
                                    <Alert severity="info" sx={{ 
                                        textAlign: 'center',
                                        borderRadius: 2,
                                        border: '1px solid #e3f2fd'
                                    }}>
                                        <Typography sx={{ fontWeight: 500, mb: 1 }}>
                                            Nenhuma configuração adicionada
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Use "Adicionar" para configurar produtos específicos ou "Importar Planilha" para importar em massa.
                                        </Typography>
                                    </Alert>
                                )}

                                <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Formato da Planilha:</strong> As colunas devem conter <strong>mlb_item</strong> (ou similares como MLB, Item ID) e <strong>price</strong> (ou similares como min_price, preço, valor)<br />
                                        <strong>Exemplo:</strong> MLB123456789 | 150.50
                                    </Typography>
                                </Alert>

                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* Progresso de processamento em lotes */}
                {processingBatches?.isProcessing && (
                    <Alert 
                        severity="info" 
                        sx={{ 
                            mt: 3, 
                            borderRadius: 2,
                            bgcolor: '#e3f2fd',
                            border: '1px solid #2196f3'
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                            🔄 Processando Settings em Lotes...
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Lote {processingBatches.current} de {processingBatches.total}
                        </Typography>
                        <LinearProgress 
                            variant="determinate" 
                            value={(processingBatches.current / processingBatches.total) * 100}
                            sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                '& .MuiLinearProgress-bar': { backgroundColor: '#2196f3' }
                            }}
                        />
                    </Alert>
                )}

                <Box sx={{ 
                    display: 'flex', 
                    gap: 3, 
                    mt: 5, 
                    justifyContent: 'flex-end',
                    pt: 3,
                    borderTop: '1px solid #e0e0e0'
                }}>
                    <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={onCancel}
                        disabled={processingBatches?.isProcessing}
                        sx={{ 
                            textTransform: 'none',
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 600
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={onSubmit}
                        disabled={processingBatches?.isProcessing}
                        sx={{ 
                            textTransform: 'none',
                            bgcolor: '#ff9800',
                            color: 'white',
                            fontWeight: 600,
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            '&:hover': { 
                                bgcolor: '#f57c00',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        {processingBatches?.isProcessing 
                            ? 'Processando...' 
                            : `${editingTrigger ? 'Atualizar' : 'Criar'} Gatilho`
                        }
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}

export default memo(TriggerForm);